/**
 * Content Library — one library (Libraries v2).
 *
 * Where an author lands from the listing or straight after creating a library. Three
 * things drive the layout, all of them consequences of how Libraries v2 actually works:
 *
 * PUBLISHING IS LIBRARY-WIDE. `commit/` publishes or discards every pending draft at
 * once. So the publish control cannot live per row, and it has to say what is in the
 * batch — especially deletions, which remove content from every course already using
 * this library the moment someone publishes. That control is the most consequential
 * thing on this page and is placed accordingly.
 *
 * EDITING A BLOCK IS THE SAME JOB AS EDITING A COURSE COMPONENT, so it is the same
 * component: `ComponentEditor`, over the same server-side probe. A block whose type still
 * ships a `studio_view` opens the platform's own editor; one whose type Verawood stripped it
 * from (`html`, `problem`) falls back to our source editor. This page used to show a raw OLX
 * textarea for everything, on the belief that `blocks/{key}/olx/` was the only write path —
 * it is not, and it was never the only editor.
 *
 * BLOCK TYPES COME FROM THE SERVER. `block_types/` is the intersection of installed
 * XBlocks and platform settings; hardcoding a list would offer authors blocks this
 * install cannot create and hide ones it can. They are shown in the same searchable palette
 * the unit page uses — shared component, separate data source, because the two endpoints
 * agree on nothing but the idea.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  EmptyState,
  Field,
  Icon,
  SearchField,
  SelectField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import { blockIcon } from '../../shared/lib/unit';
import { paletteGroups } from '../../shared/lib/palette';
import {
  LIBRARY_LICENCES,
  apiMessage,
  createBlock,
  deleteBlock,
  discardLibraryChanges,
  getLibrary,
  licenceName,
  listBlockTypeCapabilities,
  listBlockTypes,
  listBlocks,
  newBlockId,
  publishLibrary,
  updateLibrary,
  type LibraryBlock,
  type LibraryDetail as Library,
} from '../../shared/lib/library';
import { ComponentEditor } from '../outline/ComponentEditor';
import { PalettePicker } from '../outline/PalettePicker';
import { PageHeader } from '../AdminShell';
import './library-detail.css';

/** Server caps page_size at 100; 25 keeps the table scannable without paging constantly. */
const PAGE_SIZE = 25;

function when(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

export default function LibraryDetail() {
  const { libraryId } = useParams();
  const [tab, setTab] = useState('content');

  const library = useQuery({
    queryKey: ['library', libraryId],
    queryFn: () => getLibrary(libraryId as string),
    enabled: Boolean(libraryId),
  });

  if (!libraryId) {
    return (
      <>
        <PageHeader title="Library" />
        <Banner tone="danger">
          <strong>No library was named in the address.</strong> Open a library from the{' '}
          <Link to="/admin/library">library list</Link>.
        </Banner>
      </>
    );
  }

  if (library.isLoading) {
    return (
      <>
        <PageHeader title="Library" />
        <Spinner label="Loading library…" />
      </>
    );
  }

  if (library.isError || !library.data) {
    return (
      <>
        <PageHeader
          title="Library"
          actions={
            <Link className="ui-btn" data-variant="ghost" to="/admin/library">
              All libraries
            </Link>
          }
        />
        <LoadFailure error={library.error} onRetry={() => void library.refetch()} />
      </>
    );
  }

  const lib = library.data;
  const dirty = lib.has_unpublished_changes || lib.has_unpublished_deletes;
  // "No pending changes" and "published" are different facts. A brand-new library has neither
  // changes nor deletes, which made it read as green-badge Published on the same line as
  // "Never published" — and nothing in it could be linked into a course, because linking pulls
  // the published version.
  const everPublished = Boolean(lib.last_published);

  return (
    <>
      <PageHeader
        title={lib.title}
        description={lib.description || 'No description yet — add one under Settings.'}
        actions={
          <Link className="ui-btn" data-variant="ghost" to="/admin/library">
            All libraries
          </Link>
        }
      />

      <div className="libdet__ident">
        <code className="libdet__key">{lib.id}</code>
        <div className="admin__badges">
          {lib.has_unpublished_deletes && <Badge tone="danger">Unpublished deletes</Badge>}
          {lib.has_unpublished_changes && !lib.has_unpublished_deletes && (
            <Badge tone="warning">Unpublished changes</Badge>
          )}
          {!dirty && everPublished && <Badge tone="success">Published</Badge>}
          {!dirty && !everPublished && <Badge tone="neutral">Not published yet</Badge>}
          {!lib.can_edit_library && <Badge>Read only</Badge>}
        </div>
        <span className="libdet__meta">
          {lib.last_published
            ? `Last published ${when(lib.last_published)}${lib.published_by ? ` by ${lib.published_by}` : ''}`
            : 'Never published'}
          {' · '}
          {licenceName(lib.license)}
        </span>
      </div>

      {dirty && <PublishPanel library={lib} />}

      <Tabs
        label="Library sections"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'content', label: 'Content', icon: 'inventory_2', count: lib.num_blocks ?? 0 },
          { id: 'settings', label: 'Settings', icon: 'settings' },
        ]}
      />

      {tab === 'content' ? (
        <BlockList library={lib} />
      ) : (
        <SettingsPanel key={lib.id} library={lib} />
      )}
    </>
  );
}

/** Load failures split by cause, because the fix is different in each case. */
function LoadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;

  if (status === 404) {
    return (
      <Banner tone="danger">
        <strong>There is no library with that ID.</strong> It may have been deleted, or the
        address may be mistyped. Pick it from the <Link to="/admin/library">library list</Link>.
      </Banner>
    );
  }
  if (status === 403) {
    return (
      <Banner tone="danger">
        <strong>You do not have access to this library.</strong> Ask whoever administers it
        to add you to its team, then reload.
      </Banner>
    );
  }
  return (
    <Banner tone="danger">
      <strong>Could not load the library.</strong> Studio did not respond
      {status ? ` (HTTP ${status})` : ''}.{' '}
      <button type="button" className="ui-btn" data-variant="ghost" onClick={onRetry}>
        Try again
      </button>
    </Banner>
  );
}

/**
 * Publish / discard.
 *
 * Deliberately loud, and deliberately explicit about deletions: `commit/` is
 * all-or-nothing, so an author publishing one small edit also publishes every drafted
 * deletion, and that content disappears from the courses using this library. Discard is
 * the other direction and is just as irreversible, so it asks first.
 */
function PublishPanel({ library }: { library: Library }) {
  const queryClient = useQueryClient();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  /** Draft state lives on the library, its blocks, and the listing's badges. */
  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['library', library.id] });
    void queryClient.invalidateQueries({ queryKey: ['library-blocks', library.id] });
    void queryClient.invalidateQueries({ queryKey: ['studio-libraries'] });
  }

  const publish = useMutation({
    mutationFn: () => publishLibrary(library.id),
    onSuccess: invalidateAll,
  });

  const discard = useMutation({
    mutationFn: () => discardLibraryChanges(library.id),
    onSuccess: () => {
      setConfirmDiscard(false);
      invalidateAll();
    },
  });

  const failure = publish.error ?? discard.error;

  return (
    <section
      className="libdet__publish"
      data-tone={library.has_unpublished_deletes ? 'danger' : 'warning'}
    >
      <div className="libdet__publish-text">
        <h2>
          {library.has_unpublished_deletes
            ? 'Unpublished changes, including deletions'
            : 'Unpublished changes'}
        </h2>
        <p>
          {library.has_unpublished_deletes
            ? 'Publishing applies every pending change at once — including the deletions. Deleted blocks disappear from every course that uses this library. Courses keep the last published version until then.'
            : 'Courses using this library still see the last published version. Publishing applies every pending change in the library at once.'}
        </p>
        {library.last_draft_created && (
          <p className="libdet__publish-meta">
            Last edited {when(library.last_draft_created)}
            {library.last_draft_created_by ? ` by ${library.last_draft_created_by}` : ''}
          </p>
        )}
      </div>

      <div className="libdet__publish-actions">
        <Button
          variant="primary"
          busy={publish.isPending}
          disabled={!library.can_edit_library || discard.isPending}
          onClick={() => publish.mutate()}
        >
          <Icon name="publish" />
          Publish changes
        </Button>

        {confirmDiscard ? (
          <div className="libdet__confirm">
            <span>Discard every unpublished change?</span>
            <Button
              variant="danger"
              busy={discard.isPending}
              onClick={() => discard.mutate()}
            >
              Discard
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>
              Keep
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            disabled={!library.can_edit_library || publish.isPending}
            onClick={() => setConfirmDiscard(true)}
          >
            Discard changes
          </Button>
        )}
      </div>

      {!library.can_edit_library && (
        <Banner tone="info">
          <strong>You can view this library but not change it.</strong> Someone with author
          access has to publish or discard these changes.
        </Banner>
      )}

      {failure && (
        <Banner tone="danger">
          <strong>
            {publish.error ? 'The changes were not published.' : 'The changes were not discarded.'}
          </strong>{' '}
          {failure instanceof ApiError
            ? (apiMessage(failure.body) ??
              (failure.status === 403
                ? 'You do not have permission to publish this library.'
                : `Studio returned HTTP ${failure.status}. Nothing was applied — try again.`))
            : 'Studio could not be reached. Nothing was applied.'}
        </Banner>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------- content ---- */

function BlockList({ library }: { library: Library }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LibraryBlock | null>(null);

  /** Everything a write to a block can make stale: the list, the counts, the badges. */
  function invalidateContent() {
    void queryClient.invalidateQueries({ queryKey: ['library-blocks', library.id] });
    void queryClient.invalidateQueries({ queryKey: ['library', library.id] });
    void queryClient.invalidateQueries({ queryKey: ['studio-libraries'] });
  }

  const types = useQuery({
    queryKey: ['library-block-types', library.id],
    queryFn: () => listBlockTypes(library.id),
    enabled: library.can_edit_library,
    staleTime: 5 * 60_000,
  });

  /**
   * The one fact `block_types/` withholds: which of those types hold children.
   *
   * A separate query on purpose. It is platform-wide rather than per-library, so it is
   * fetched once and reused, and a failure here must not take the palette down with it —
   * see `paletteGroups` in shared/lib/palette.ts, which offers everything when the answer
   * is missing.
   */
  const capabilities = useQuery({
    queryKey: ['block-type-capabilities'],
    queryFn: listBlockTypeCapabilities,
    enabled: library.can_edit_library,
    staleTime: 30 * 60_000,
  });

  // The SAME function the unit page calls, differing only in which endpoint's answer it is
  // handed. Every rule about what a palette offers — the has_children exclusion and its
  // fail-open, the preference order, what sits behind the disclosure — is in there, once.
  const groups = useMemo(
    () => paletteGroups({ surface: 'library', types: types.data ?? [] }, capabilities.data),
    [types.data, capabilities.data],
  );

  /**
   * Create, then edit — no name asked first.
   *
   * The block is created immediately on a pick, exactly as the unit page's palette does, and
   * its editor opens on top. Naming it belongs in that editor: the old form asked for a name
   * before the block existed and used it as the permanent ID, which welded a title an author
   * would want to change to an identifier that can never change.
   */
  const create = useMutation({
    mutationFn: (blockType: string) =>
      createBlock(library.id, blockType, newBlockId(blockType)),
    onSuccess: (block) => {
      setAdding(false);
      invalidateContent();
      setEditing(block);
    },
  });

  const blocks = useQuery({
    queryKey: ['library-blocks', library.id, page, search],
    queryFn: () => listBlocks(library.id, { page, pageSize: PAGE_SIZE, search: search || undefined }),
    // Keeps the previous page on screen while the next one loads, instead of collapsing
    // the table to a spinner on every keystroke and page step.
    placeholderData: keepPreviousData,
  });

  function onSearch(value: string) {
    setSearch(value);
    setPage(1);
  }

  const columns: Column<LibraryBlock>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (block) => (
        <Chip icon={blockIcon(block.block_type)}>{block.block_type}</Chip>
      ),
    },
    {
      key: 'name',
      header: 'Block',
      render: (block) => (
        <>
          <span className="libdet__block-name">{block.display_name || 'Untitled'}</span>
          <div className="admin__sub">{block.id}</div>
        </>
      ),
    },
    {
      key: 'state',
      header: 'State',
      // Same distinction as the library's own badge, and it matters more here: a block that has
      // never been published cannot be linked into a course at all, so "Draft" would understate
      // it. The picker greys such blocks out for exactly this reason.
      render: (block) =>
        !block.last_published ? (
          <Badge tone="neutral">Not published</Badge>
        ) : block.has_unpublished_changes ? (
          <Badge tone="warning">Draft changes</Badge>
        ) : (
          <Badge tone="success">Published</Badge>
        ),
    },
    {
      key: 'edited',
      header: 'Last edited',
      render: (block) => (
        <>
          {when(block.modified)}
          {block.last_draft_created_by && (
            <div className="admin__sub libdet__by">{block.last_draft_created_by}</div>
          )}
        </>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (block) => (
        <BlockRowActions
          library={library}
          block={block}
          onEdit={() => setEditing(block)}
          onDeleted={() => {
            if (editing?.id === block.id) setEditing(null);
          }}
        />
      ),
    },
  ];

  const rows = blocks.data?.results ?? [];
  const total = blocks.data?.count ?? 0;
  const pages = blocks.data?.num_pages ?? 1;

  return (
    <>
      <Toolbar>
        <div className="toolbar__end">
          <SearchField value={search} onChange={onSearch} placeholder="Search blocks…" />
          {library.can_edit_library && (
            <Button
              variant="primary"
              // The types are fetched when the page loads, so this is only ever disabled for
              // the moment after a cold navigation — and a palette that opens empty reads as
              // "this library accepts nothing", which would be a lie about a pending request.
              disabled={types.isLoading}
              onClick={() => setAdding(true)}
            >
              <Icon name="add" />
              Add block
            </Button>
          )}
        </div>
      </Toolbar>

      {create.isError && (
        <Banner tone="danger">
          <strong>The block was not created.</strong>{' '}
          {create.error instanceof ApiError
            ? (apiMessage(create.error.body) ??
              `Studio returned HTTP ${create.error.status}.`)
            : 'Studio could not be reached.'}{' '}
          Nothing was added to this library.
        </Banner>
      )}

      {adding &&
        (types.isError ? (
          <Banner tone="danger">
            <strong>Could not read the available block types.</strong> Studio did not answer, so
            there is nothing safe to offer. Reload the page to try again.
          </Banner>
        ) : groups.length === 0 ? (
          <Banner tone="warning">
            <strong>This platform offers no block types for libraries.</strong> An operator has
            to enable at least one XBlock type in the platform&rsquo;s advanced settings.
          </Banner>
        ) : (
          <PalettePicker
            title="Add to this library"
            groups={groups}
            pending={create.isPending ? `add:${create.variables}:` : null}
            // The palette stays open across a failure, and it is in the top layer, so the
            // page's own banner is covered. The explanation has to come inside.
            error={
              create.isError ? (
                <Banner tone="danger">
                  <strong>The block was not created.</strong>{' '}
                  {create.error instanceof ApiError
                    ? (apiMessage(create.error.body) ??
                      `Studio returned HTTP ${create.error.status}.`)
                    : 'Studio could not be reached.'}{' '}
                  Nothing was added to this library.
                </Banner>
              ) : undefined
            }
            advancedNote="Advanced types are whatever XBlocks an operator installed and enabled."
            onPick={(template) => create.mutate(template.category)}
            onClose={() => setAdding(false)}
          />
        ))}

      {editing && (
        <ComponentEditor
          key={editing.id}
          blockKey={editing.id}
          blockName={editing.display_name || editing.id}
          publishTarget="library"
          readOnly={!library.can_edit_library}
          onClose={() => setEditing(null)}
          onSaved={invalidateContent}
        />
      )}

      {blocks.isLoading ? (
        <Spinner label="Loading blocks…" />
      ) : blocks.isError ? (
        <Banner tone="danger">
          <strong>Could not list the blocks in this library.</strong>{' '}
          {blocks.error instanceof ApiError
            ? `Studio returned HTTP ${blocks.error.status}.`
            : 'Studio did not respond.'}{' '}
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            onClick={() => void blocks.refetch()}
          >
            Try again
          </button>
        </Banner>
      ) : rows.length === 0 && !search ? (
        <Card>
          <EmptyState
            title="This library is empty"
            body="Add a block and it becomes available to every course in this academy — once you publish it."
            action={
              library.can_edit_library && !adding ? (
                <Button variant="primary" onClick={() => setAdding(true)}>
                  <Icon name="add" />
                  Add the first block
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(block) => block.id}
            empty={`No block matches “${search}”.`}
          />
        </Card>
      )}

      {pages > 1 && (
        <div className="libdet__pager">
          <Button
            variant="ghost"
            disabled={page <= 1 || blocks.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <Icon name="chevron_left" />
            Previous
          </Button>
          <span>
            Page {blocks.data?.current_page ?? page} of {pages} · {total} blocks
          </span>
          <Button
            variant="ghost"
            disabled={page >= pages || blocks.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
            <Icon name="chevron_right" />
          </Button>
        </div>
      )}
    </>
  );
}

/** Per-row Edit / Delete. Delete confirms in place — it is a draft delete, but still. */
function BlockRowActions({
  library,
  block,
  onEdit,
  onDeleted,
}: {
  library: Library;
  block: LibraryBlock;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => deleteBlock(block.id),
    onSuccess: () => {
      onDeleted();
      void queryClient.invalidateQueries({ queryKey: ['library-blocks', library.id] });
      // num_blocks and has_unpublished_deletes both change with this.
      void queryClient.invalidateQueries({ queryKey: ['library', library.id] });
      void queryClient.invalidateQueries({ queryKey: ['studio-libraries'] });
    },
  });

  if (!library.can_edit_library) {
    return (
      <div className="admin__row-actions">
        <button type="button" className="ui-btn" data-variant="ghost" onClick={onEdit}>
          View source
        </button>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="admin__row-actions libdet__confirm">
        <span>Delete?</span>
        <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
          Delete
        </Button>
        <Button variant="ghost" onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="admin__row-actions">
      <button type="button" className="ui-btn" data-variant="ghost" onClick={onEdit}>
        Edit
      </button>
      <button
        type="button"
        className="ui-btn"
        data-variant="ghost"
        onClick={() => setConfirming(true)}
      >
        Delete
      </button>
      {remove.isError && (
        <span className="libdet__row-error" role="alert">
          Not deleted
          {remove.error instanceof ApiError ? ` (HTTP ${remove.error.status})` : ''}
        </span>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- settings ---- */

function SettingsPanel({ library }: { library: Library }) {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(library.title);
  const [description, setDescription] = useState(library.description ?? '');
  const [license, setLicense] = useState(library.license ?? '');
  const [publicRead, setPublicRead] = useState(library.allow_public_read);
  const [publicLearning, setPublicLearning] = useState(library.allow_public_learning);

  const readOnly = !library.can_edit_library;

  // Blank is not "clear" here: title and description are non-blank CharFields on the
  // server, so an empty string comes back as a 400. Say so before the round trip.
  const titleError = title.trim() === '' ? 'A title is required.' : undefined;
  const descriptionError =
    description.trim() === '' && (library.description ?? '') !== ''
      ? 'The platform will not accept an empty description. Write at least one character.'
      : undefined;

  const changes = {
    ...(title.trim() !== library.title ? { title: title.trim() } : {}),
    ...(description !== (library.description ?? '') ? { description } : {}),
    ...(license !== (library.license ?? '') ? { license } : {}),
    ...(publicRead !== library.allow_public_read ? { allow_public_read: publicRead } : {}),
    ...(publicLearning !== library.allow_public_learning
      ? { allow_public_learning: publicLearning }
      : {}),
  };
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: () => updateLibrary(library.id, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['library', library.id] });
      // The listing shows title and state, so it is stale the moment this succeeds.
      void queryClient.invalidateQueries({ queryKey: ['studio-libraries'] });
    },
  });

  return (
    <Card title="Library settings">
      {readOnly && (
        <Banner tone="info">
          <strong>These settings are read-only for you.</strong> You can see how the library
          is configured, but only someone with author access can change it.
        </Banner>
      )}

      <form
        className="libdet__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !titleError && !descriptionError) save.mutate();
        }}
      >
        <Field
          label="Title"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={titleError}
          disabled={readOnly}
          required
        />

        <div className="ui-field" data-invalid={descriptionError ? 'true' : undefined}>
          <label htmlFor="library-description">Description</label>
          <textarea
            id="library-description"
            className="libdet__textarea"
            rows={4}
            value={description}
            disabled={readOnly}
            onChange={(e) => setDescription(e.target.value)}
            aria-describedby={
              descriptionError ? 'library-description-error' : 'library-description-hint'
            }
          />
          {descriptionError ? (
            <p className="ui-field__error" id="library-description-error" role="alert">
              {descriptionError}
            </p>
          ) : (
            <p className="ui-field__hint" id="library-description-hint">
              Helps other authors decide whether this is the library they want.
            </p>
          )}
        </div>

        <SelectField
          label="Content licence"
          name="license"
          value={license}
          onChange={(e) => setLicense(e.target.value)}
          options={LIBRARY_LICENCES}
          disabled={readOnly}
        />

        <fieldset className="libdet__exposure" disabled={readOnly}>
          <legend>Access outside the academy</legend>
          <p className="libdet__warn">
            <Icon name="public" />
            Both of these publish content beyond this academy. Anyone with the link — not
            just your learners, and not just signed-in people — can reach what you turn on
            here. Leave them off unless the content is meant to be public.
          </p>

          <label className="libdet__check">
            <input
              type="checkbox"
              checked={publicRead}
              disabled={readOnly}
              onChange={(e) => setPublicRead(e.target.checked)}
            />
            <span>
              <strong>Public read</strong>
              <small>
                Anyone can read this library's blocks and their source, including drafts
                once published. They do not have to belong to this academy.
              </small>
            </span>
          </label>

          <label className="libdet__check">
            <input
              type="checkbox"
              checked={publicLearning}
              disabled={readOnly}
              onChange={(e) => setPublicLearning(e.target.checked)}
            />
            <span>
              <strong>Public learning</strong>
              <small>
                Anyone can work through this content directly, outside any course — answers
                and all. Use it for material you would happily publish on the open web.
              </small>
            </span>
          </label>
        </fieldset>

        {save.isError && (
          <Banner tone="danger">
            <strong>The settings were not saved.</strong>{' '}
            {save.error instanceof ApiError
              ? (apiMessage(save.error.body) ??
                (save.error.status === 403
                  ? 'You do not have permission to change this library.'
                  : `Studio returned HTTP ${save.error.status}. Nothing was changed.`))
              : 'Studio could not be reached. Nothing was changed.'}
          </Banner>
        )}

        {save.isSuccess && !dirty && (
          <Banner tone="success">
            <strong>Settings saved.</strong> Library settings apply immediately — they are
            not part of the publish cycle.
          </Banner>
        )}

        {!readOnly && (
          <div className="libdet__form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={save.isPending}
              disabled={!dirty || Boolean(titleError) || Boolean(descriptionError)}
            >
              Save settings
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                onClick={() => {
                  setTitle(library.title);
                  setDescription(library.description ?? '');
                  setLicense(library.license ?? '');
                  setPublicRead(library.allow_public_read);
                  setPublicLearning(library.allow_public_learning);
                }}
              >
                Undo changes
              </Button>
            )}
          </div>
        )}

        <p className="libdet__note">
          The library ID <code>{library.id}</code> is permanent. Courses reference blocks by
          it, so it cannot be renamed — create a new library if you need a different one.
        </p>
      </form>
    </Card>
  );
}
